import React, { useEffect, useRef } from 'react';
import { Skeleton } from 'primereact/skeleton';
import '../BreadcrumbSection/BreadcrumbSection.styles.scss';
import '../PageTabs/PageTabs.styles.scss';
import '../ActionPanel/ActionPanel.styles.scss';
import '../DataFooter/DataFooter.styles.scss';
import '../../../global.styles.scss';
import './ProductAnalysisSkeleton.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const ProductAnalysisSkeleton: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate wrapper height based on scaled content to prevent extra white space
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const updateWrapperHeight = () => {
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;
        
        const contentHeight = contentRef.current.scrollHeight || 0;
        const scaledHeight = contentHeight * 0.65;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      });
    };

    const timeoutId = setTimeout(updateWrapperHeight, 100);
    window.addEventListener('resize', updateWrapperHeight);
    
    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });
    
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="product-analysis-page-wrapper" ref={wrapperRef}>
      <div className="product-analysis-page" ref={contentRef}>
      {/* Breadcrumb Section Skeleton */}
      <nav className="product-analysis-breadcrumb">
        <div className="container">
          <Skeleton width="25rem" height="1.5rem" />
        </div>
      </nav>

      {/* Page Tabs Skeleton */}
      <nav className="page-tabs">
        <div className="container">
          <ul className="tabs-nav">
            {[1, 2, 3, 4, 5, 6].map(index => (
              <li key={index}>
                <Skeleton width="8rem" height="3rem" borderRadius="0.5rem" />
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Content Area Skeleton */}
      <div className="container">
        <Skeleton
          width="100%"
          height="50rem"
          borderRadius="0.75rem"
          style={{ marginTop: '2rem' }}
        />
      </div>

      {/* Action Panel Skeleton */}
      <div className="action-panel visible">
        <div className="action-buttons">
          <Skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
          <Skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
          <Skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
          <Skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
        </div>
      </div>

      {/* Data Footer Skeleton */}
      <footer className="data-footer">
        <div className="container">
          <div className="data-sources">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(index => (
              <div key={index} className="data-source">
                <Skeleton width="12rem" height="1.5rem" />
              </div>
            ))}
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default ProductAnalysisSkeleton;
